import { useRef, useEffect, memo, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { FeedPost } from '@/components/feed/FeedPost';
import type { FeedPostData } from '@/types/polls';

interface VirtualizedFeedProps {
  posts: FeedPostData[];
  className?: string;
  estimatePostHeight?: number;
}

/**
 * Virtualized feed for displaying large lists of poll posts.
 * Only renders visible posts + a small overscan buffer for smooth scrolling.
 * 
 * NOTE: This component needs to find its scroll parent (the overflow-y-auto container).
 */
export const VirtualizedFeed = memo(function VirtualizedFeed({
  posts,
  className,
  estimatePostHeight = 480,
}: VirtualizedFeedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetPollId = searchParams.get('poll');
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  
  // NOTE: Keep scroll parent in state so the virtualizer re-initializes once detected.
  // Using only a ref can leave the list blank until some other state change triggers a re-render.
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  // Find the scrollable parent on mount
  useEffect(() => {
    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          setScrollParent(parent);
          break;
        }
        parent = parent.parentElement;
      }
    }
  }, []);

  const virtualizer = useVirtualizer({
    count: posts.length,
    getScrollElement: () => scrollParent,
    estimateSize: () => estimatePostHeight,
    overscan: 2, // Render 2 extra posts above/below viewport
  });

  // Scroll to highlighted poll when navigating from notification or after creating a poll.
  // Uses reactive targetPollId from useSearchParams so re-navigation to ?poll=xxx triggers scroll.
  useEffect(() => {
    if (targetPollId && posts.length > 0 && scrollParent) {
      const pollIndex = posts.findIndex(p => p.id === targetPollId);

      if (pollIndex !== -1) {
        virtualizer.scrollToIndex(pollIndex, { align: 'start', behavior: 'smooth' });
        setHighlightedIndex(pollIndex);

        const timer = setTimeout(() => {
          setHighlightedIndex(null);
          navigate('.', { replace: true });
        }, 2500);

        return () => clearTimeout(timer);
      }
    }
  }, [targetPollId, posts, scrollParent, virtualizer, navigate]);

  const virtualItems = virtualizer.getVirtualItems();

  // If less than 10 posts, don't virtualize (overhead not worth it)
  if (posts.length < 10) {
    return (
      <div className={cn('pb-nav divide-y divide-white/5', className)}>
        {posts.map((post, index) => (
          <div
            key={post.id}
            id={`poll-${post.id}`}
            style={{ animationDelay: `${index * 100}ms`, contentVisibility: 'auto', containIntrinsicSize: 'auto 480px' }}
            className={cn(
              'transition-all duration-300',
              highlightedIndex === index && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
            )}
          >
            <FeedPost post={post} isFirstInFeed={index === 0} priority={index < 3} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('w-full pb-nav', className)}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const post = posts[virtualRow.index];
          const isHighlighted = highlightedIndex === virtualRow.index;
          
          return (
            <div
              key={virtualRow.key}
              id={`poll-${post.id}`}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className={cn(
                'border-b border-white/5 transition-all duration-300',
                isHighlighted && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
              )}
            >
              <FeedPost post={post} isFirstInFeed={virtualRow.index === 0} priority={virtualRow.index < 2} />
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default VirtualizedFeed;
